import { FinalForm, useStackApi } from "@comet/admin";
import { Save } from "@mui/icons-material";
import { Button } from "@mui/material";
import { ContentWrapper, type ContentWrapperProps } from "@src/components/ContentWrapper";
import { type AnyObject, type FormApi, type SubmissionErrors } from "final-form";
import { type FC, type PropsWithChildren, type ReactNode } from "react";
import { type FormProps, useFormState } from "react-final-form";
import { FormattedMessage } from "react-intl";

import { FormButtonsWrapper } from "./Form.sc";

const FormButtons: FC<PropsWithChildren> = () => {
    const { pristine, hasValidationErrors, submitting } = useFormState();

    return (
        <FormButtonsWrapper>
            <Button color="primary" variant="contained" startIcon={<Save />} disabled={pristine || hasValidationErrors || submitting} type="submit">
                <FormattedMessage id="reviermanager.button.save" defaultMessage="Speichern" />
            </Button>
        </FormButtonsWrapper>
    );
};

interface Props<FormValues = AnyObject> extends FormProps<FormValues> {
    mode: "edit" | "add";
    onSubmit: (values: FormValues, form: FormApi<FormValues>) => SubmissionErrors | Promise<SubmissionErrors | undefined> | undefined | void;
    onAfterSubmit?: (values: FormValues, form: FormApi<FormValues>) => void;
    link?: ReactNode;
    customButtons?: ReactNode;
}

const Form: FC<PropsWithChildren<Props & ContentWrapperProps>> = ({ children, title, link, customButtons, mode, onAfterSubmit, ...props }) => {
    const stackApi = useStackApi();

    // After creating a new record the form should return to the table. Comet no longer navigates back by
    // default on submit, so restore that behaviour centrally for add forms. Callers can opt out (e.g. edit
    // forms that stay on the detail page) by passing their own onAfterSubmit.
    const handleAfterSubmit = onAfterSubmit ?? (mode === "add" ? () => stackApi?.goBack() : undefined);

    return (
        <ContentWrapper title={title} link={link}>
            <FinalForm {...props} mode={mode} onAfterSubmit={handleAfterSubmit}>
                {children}
                {customButtons ? customButtons : <FormButtons />}
            </FinalForm>
        </ContentWrapper>
    );
};

export default Form;
